import { useState, useCallback } from 'react';
import { useMediaResource } from '@brightmls/nextgen.lae.flc';
import { MediaCategory, MediaResource } from '../../../../system/media';
import { useReso } from '../../../../contexts/ResoContext';
import { useMetadata } from '../../../../contexts/MetadataContext';
import { translateMediaRecord } from '../../../../system/media';
import { Logger } from '../../../../utils/logger';

export interface UseResoMediaOptions {
  listingKey: string;
  onFetchComplete?: () => void;
  onFetchError?: (error: Error) => void;
  logger?: Logger;
}

/**
 * Hook to fetch photo media from RESO and update local state
 * Uses the updateMedia function from useSyncedMediaResource to replace local photo state
 * with fresh data from the server
 */
export const useResoMedia = (options: UseResoMediaOptions) => {
  const {
    listingKey,
    onFetchComplete,
    onFetchError,
    logger: loggerProp,
  } = options;
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState<Error | null>(null);
  const { executeQuery } = useReso();
  const { locale } = useMetadata();

  // Get updateMediaByCategory function from FLC hook to replace all photos
  const { updateMediaByCategory } = useMediaResource();

  // Default logger fallback
  const logger = loggerProp || {
    log: console.log,
    error: console.error,
    warn: console.warn,
  };

  /**
   * Fetch fresh photo media from RESO server and update local state
   * Only fetches MediaCategory.Photo - does not affect documents, tours, or floor plans
   */
  const fetchResoMedia = useCallback(async () => {
    if (!listingKey) {
      const error = new Error('No listing key provided for RESO media fetch');
      setFetchError(error);
      onFetchError?.(error);
      return;
    }

    setIsFetching(true);
    setFetchError(null);

    try {
      logger.log('Starting RESO media fetch for listing:', listingKey);

      // Query photo media for this listing
      const result = await executeQuery<any>('CWA_Media', {
        queryParams: {
          $filter: `CWAResourceRecordKey eq '${listingKey}' and CWAMediaCategory eq 'Photo'`,
          $format: 'json',
        },
      });

      logger.log('CWA_Media query result:', result);

      if (!result || !result.value || !Array.isArray(result.value)) {
        throw new Error('Invalid response from media query');
      }

      logger.log(
        `Fetched ${result.value.length} photo media items from RESO server`,
      );

      // Get translation object
      const translation = locale();

      // Translate all photo media items
      const translatedMediaItems = result.value
        .filter((mediaItem: any) => mediaItem.CWAMediaCategory === 'Photo')
        .map((mediaItem: any) => translateMediaRecord(mediaItem, translation));

      logger.log(`Translated ${translatedMediaItems.length} photo media items`);

      // Replace all photo media with fresh data from server
      logger.log('Updating media context with fetched photos...');
      updateMediaByCategory({
        category: MediaCategory.Photo,
        resources: translatedMediaItems as MediaResource[],
      });

      logger.log('Successfully fetched photo media from RESO server');
      onFetchComplete?.();
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      logger.error('Failed to fetch photo media from RESO:', err);
      setFetchError(err);
      onFetchError?.(err);
    } finally {
      setIsFetching(false);
    }
  }, [
    listingKey,
    executeQuery,
    updateMediaByCategory,
    locale,
    onFetchComplete,
    onFetchError,
    logger,
  ]);

  return {
    fetchResoMedia,
    isFetching,
    fetchError,
  };
};
